import React, {FC, useCallback, useLayoutEffect, useMemo, useState} from 'react';
import type {RootState} from "../../@types/store";
import ErrorBoundaryO from "../../ErrorBoundary/ErrorBoundaryO";
import {connect} from "react-redux";
import Task, {AssignedToType} from "../../entities/task";
import CopyText from "../../ui/CopyText";
import IconButton from "../../ui/IconButton/IconButton";
import Icons from "../../ui/IconButton/Icons";
import {format} from "date-fns";
import {getCurrentUserId} from "../../lib/picos/currentLogin";
import {formatUserName, formatUserNameFromId} from "../../Contacts/ContactFormat";
import {taskFailureReason} from "../../TaskManagement/TaskHelper";
import HtmlDialog from "../../ui/dialogs/HtmlDialog";
import EntityMeta from "../../ui/EntityMeta/EntityMeta";
import util from "../../lib/util";
import {Status, uuid} from "../../@types/entity";
import * as DateUtil from "../../lib/dateutils";
import TaskItem from "../../TaskManagement/TaskItem";
import {saveTaskQueue} from '../../store/actions/taskActions';
import {useRootDispatch} from "../../hooks/useOutlawStore";
import newHRTimestamp from "../../lib/picos/timestamp";
import EditableTable, {EditableTableProps} from "../../ui/EditableTable/EditableTable";
import useComponentCache from "../../hooks/useComponentCache";
import {BAD_TASKS_SORT_PROPS} from "./badTasksSortProps";

type BadTasksState = {
    editTask: Task | null,
    auditTask: Task | null,
}

const BadTasks: FC<BadTasksProps> = props => {
    const {cacheSave, cacheValue} = useComponentCache<BadTasksState>('bad-tasks');

    const dispatch = useRootDispatch();

    const [editTask, setEditTask] = useState<Task | null>(cacheValue('editTask', null));
    const [auditTask, setAuditTask] = useState<Task | null>(cacheValue('auditTask', null));

    // Save cache
    useLayoutEffect(() => {
        cacheSave({editTask, auditTask});
    }, [auditTask, cacheSave, editTask]);

    const handleTaskOk = useCallback((task: Task) => {
        const taskClone = task.clone();
        taskClone.status = Status.Update;
        taskClone.addedBy = getCurrentUserId();
        taskClone.addedOn = newHRTimestamp();
        dispatch(saveTaskQueue(taskClone));

        setEditTask(null);
    }, [dispatch, setEditTask]);

    const handleTaskCancel = useCallback(() => {
        setEditTask(null);
    }, [setEditTask]);

    const getAssignedTo = useCallback((task: Task) => {
        let {assignedToType, assignedTo} = task;
        if (!assignedTo && assignedToType === AssignedToType.User) assignedTo = getCurrentUserId();
        if (assignedToType === AssignedToType.User) {
            const user = props.users.get(assignedTo);
            const userName = user ? formatUserName(user) : '';
            return <IconButton type={Icons.USER} label={userName ?? <CopyText useCodeStyle={true} text={assignedTo}/>}/>;
        }
        else {
            const group = props.groups.get(assignedTo);
            return <IconButton type={Icons.GROUP} label={group?.name ?? <CopyText useCodeStyle={true} text={assignedTo} />} color='navy' textColor='navy'/>;
        }
    }, [props.groups, props.users]);

    const tasks = useMemo(() => {
        return props.tasks
                .filter((task: Task) => task.status !== Status.Delete)
                .filter((task: Task) => taskFailureReason(task) !== null)
        }, 
        [props.tasks] // Do not change!
    );

    const renderId = useCallback((task: Task) => {
        return <CopyText text={task.id} useCodeStyle={true}/>
    }, [])

    const renderDueDate = useCallback((task: Task) => {
        return task.dueDate? format(task.dueDate, 'yyyy-MM-dd') : '';
    }, []);

    const renderCompletionDate = useCallback((task: Task) => {
        return task.completionDate? format(task.completionDate, 'yyyy-MM-dd') : '';
    }, []);

    const renderReasonCode = useCallback((task: Task) => {
        return taskFailureReason(task)?.code;
    }, []);

    const renderReasonMessage = useCallback((task: Task) => {
        return taskFailureReason(task)?.message;
    }, []);
    
    if (props.contacts.length === 0 || props.users.length === 0 || props.tasks.length === 0) return null;

    const BadTransactionsTable = EditableTable as unknown as React.ComponentType<Partial<EditableTableProps<Task>>>;
    
    return <ErrorBoundaryO uniqueKey='bad-tasks'>
        <section>
			<div className='support-form__editable-table-container'>
                <BadTransactionsTable name='Bad Tasks'
                                      entityName='task'
                                      urlQueryKey="bad-task"
                                      items={tasks}
                                      editor={TaskItem}
                                      properties={[
                                          {label: 'id', displayLabel: 'ID', width: '1fr', renderItemFn: renderId},
                                          {label: 'title', width: '1fr'},
                                          {label: 'invoiceNotes', displayLabel: 'Description', width: '1fr'},
                                          {label: 'dueDate', width: '1fr', renderItemFn: renderDueDate},
                                          {label: 'completionDate', width: '1fr', renderItemFn: renderCompletionDate},
                                          {label: 'assignedTo', width: '1fr', renderItemFn: getAssignedTo},
                                          {label: 'reasonCode', displayLabel: 'Code', renderItemFn: renderReasonCode},
                                          {label: 'reasonMessage', displayLabel: 'Message', width: '2fr', renderItemFn: renderReasonMessage},
                                      ]}
                                      sortProps={BAD_TASKS_SORT_PROPS}
                                      onEdit={handleTaskOk}
                                      hideGridOnEdit={true}
                                      otherActions={[
                                          {
                                              label: 'Audit Log',
                                              selectedIcon: Icons.HISTORY,
                                              color: 'navy',
                                              action: (task: Task) => setAuditTask(task),
                                          }
                                      ]}
                />
            </div>

            {/* Show Task dialog if editing this transaction */}
            <div>
				<HtmlDialog title='Edit Task' isShown={!!editTask} onClose={() => setEditTask(null)}>
					<TaskItem {...props}
                              current={editTask}
                              onOk={handleTaskOk}
                              onCancel={handleTaskCancel}
                              hideFinancialSummary={true}
                              embedded={true}
                    />
				</HtmlDialog>
			</div>

            {/* Show Blame Log if selected */}
            {(props.misc.isConnected && auditTask) && <div>
				<HtmlDialog title='Edit Task' isShown={true} onClose={() => setAuditTask(null)}>
					<EntityMeta entityType="task"
					            entity={auditTask}
					            startOpen={true}
                                showButton={false}
					            onVersionChange={(e) => setAuditTask(e as Task)}
					/>
				</HtmlDialog>
			</div>}
		</section>
    </ErrorBoundaryO>;
};

export type BadTasksProps = Pick<RootState, 'contacts' | 'invoices' | 'tasks' | 'misc' | 'dockets' | 'groups' | 'users'>;


const mapStateToProps = (state: RootState) => ({
    contacts: state.contacts,
    dockets: state.dockets,
    groups: state.groups,
    invoices: state.invoices,
    tasks: state.tasks,
    misc: state.misc,
    users: state.users,
})

export default connect(mapStateToProps)(BadTasks);